import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Stack, Box, Popover, Button, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import EventIcon from "@mui/icons-material/Event";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { saveAs } from "file-saver";

const CardDashboard = (props) => {
  const navigation = useNavigate();
  const [currentStatus, setCurrentStatus] = React.useState(null);

  // Popover anchor for the extend-deadline picker
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deadline, setDeadline] = React.useState("");
  const [extending, setExtending] = React.useState(false);
  const [extendMsg, setExtendMsg] = React.useState("");

  // ── fetch current status ──────────────────────────────────────────────────
  React.useEffect(() => {
    if (!props.id) return;

    fetch(`${process.env.REACT_APP_BASE_URL}/api/report/${props.id}/steps`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCurrentStatus(data[0].status);
        } else {
          setCurrentStatus("New");
        }
      })
      .catch(() => setCurrentStatus("New"));
  }, [props.id]);

  // ── download ─────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    const backendUrl =
      localStorage.getItem("role") === "ADMIN"
        ? `${process.env.REACT_APP_BASE_URL}/api/admin/report/${props.currentYear}/${props.currentQuarter}/download`
        : `${process.env.REACT_APP_BASE_URL}/api/report/${props.currentYear}/${props.currentQuarter}/download`;

    axios
      .get(backendUrl, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/zip" });
        saveAs(blob, "downloaded.zip");
      })
      .catch((error) => console.error("Error downloading zip file:", error));
  };

  // ── generate report ───────────────────────────────────────────────────────
  const handleGenerateReport = async () => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      };
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/report/generateReport/${props.currentYear}/${props.currentQuarter}`,
        requestOptions
      );
      console.log("Report generated successfully:", response);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  // ── extend all deadlines ──────────────────────────────────────────────────
  const handleExtendAll = async () => {
    if (!deadline) {
      setExtendMsg("Please pick a date & time.");
      return;
    }
    setExtending(true);
    setExtendMsg("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/report/extend/${props.currentYear}/${props.currentQuarter}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          body: JSON.stringify({ deadline: new Date(deadline).toISOString() }),
        }
      );
      const data = await res.json();
      setExtendMsg(data.message || "All deadlines updated ✓");
    } catch (err) {
      console.error(err);
      setExtendMsg("Failed to update deadlines.");
    } finally {
      setExtending(false);
    }
  };

  // ── edit navigation ───────────────────────────────────────────────────────
  const handleEditNavigation = () => {
    const department = localStorage.getItem("departmentId");
    const role = localStorage.getItem("role");

    if (role === "REPRESENTATIVE") {
      const routes = {
        AUTONOMOUS: `/autoEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        PLACEMENT: `/placementEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        PA: `/paEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        ACADEMIC: `/academicEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        COE: `/coeEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        IQAC: `/deanEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        NCC: `/nccEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        NSS: `/nssEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        PED: `/pedEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        ADMINISTRATOR: `/administratorEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        IIC: `/iicEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        ACCOUNT: `/accountEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        "PSGII - TRAINING DEPT": `/psgTrainingEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
        "ALUMNI ASSN": `/aluminiEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`,
      };
      navigation(routes[department] || `/sec/${props.id}/${props.currentYear}/${props.currentQuarter}`);
    } else if (role === "HOD") {
      navigation(`/hodEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`);
    } else if (role === "ADMIN") {
      navigation(`/AdminEdit/${props.currentYear}/${props.currentQuarter}`);
    }
  };

  // ── helpers ───────────────────────────────────────────────────────────────
  const isActive = props.stage !== "Closed" && props.stage !== "Completed";
  const isAdmin = localStorage.getItem("role") === "ADMIN";

  const ActionIcon = ({ enabled, onClick, children }) => (
    <Box
      onClick={enabled ? onClick : undefined}
      sx={{
        border: 1,
        borderRadius: "3px",
        padding: "2px",
        borderColor: enabled ? "#FFFFFF" : "#4C4E4E",
        cursor: enabled ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        transition: "background 0.2s",
        "&:hover": enabled
          ? { backgroundColor: "rgba(255,255,255,0.15)" }
          : {},
      }}
    >
      {React.cloneElement(children, {
        style: { color: enabled ? "#FFFFFF" : "#4C4E4E", fontSize: 20 },
      })}
    </Box>
  );

  return (
    <Card
      sx={{
        minWidth: 275,
        backgroundColor: isActive ? "#067094" : "#14475D",
        boxShadow: "0 4px 6px #FFFFFF",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: "0 8px 12px #FFFFFF",
        },
      }}
    >
      <CardContent>
        {/* Title */}
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            textTransform: "lowercase",
            "&:first-letter": { textTransform: "capitalize" },
            fontWeight: "bold",
            color: "#FFFFFF",
          }}
        >
          Half
        </Typography>

        {/* Big number */}
        <Typography
          variant="h1"
          sx={{
            mb: 1.5,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: "bold",
            background: "linear-gradient(to bottom, #2B596D,#FFFFFF)",
            WebkitBackgroundClip: "text",
            color: "transparent",
            display: "inline-block",
          }}
        >
          0{props.index}
        </Typography>

        {/* Status + actions row */}
        <Stack direction="row" spacing={10}>
          {/* Status label */}
          <Typography
            variant="body2"
            sx={{ fontFamily: "Montserrat, sans-serif", color: "#FFFFFF" }}
          >
            {props.stage === "Awaiting" ? currentStatus : props.stage}
          </Typography>

          {/* Action icons */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Edit */}
            <ActionIcon enabled={props.isEdit} onClick={handleEditNavigation}>
              <EditIcon />
            </ActionIcon>

            {/* Download */}
            <ActionIcon enabled={props.isDownload} onClick={handleDownload}>
              <DownloadIcon />
            </ActionIcon>

            {/* Generate report (admin only) */}
            {isAdmin && (
              <ActionIcon enabled={!!props.isReport} onClick={handleGenerateReport}>
                <FileCopyIcon />
              </ActionIcon>
            )}

            {/* ── Extend All Deadlines (admin only) ── */}
            {isAdmin && (
              <>
                <ActionIcon
                  enabled={isActive}
                  onClick={(e) => {
                    if (isActive) {
                      setExtendMsg("");
                      setAnchorEl(e.currentTarget);
                    }
                  }}
                >
                  <EventIcon />
                </ActionIcon>

                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => {
                    setAnchorEl(null);
                    setExtendMsg("");
                    setDeadline("");
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      backgroundColor: "#0d2f3f",
                      border: "1px solid #067094",
                      borderRadius: "8px",
                      padding: "16px",
                      minWidth: 280,
                    },
                  }}
                >
                  {/* Popover header */}
                  <Typography
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 700,
                      color: "#79DCFF",
                      fontSize: "0.85rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 1.5,
                    }}
                  >
                    Extend All Deadlines
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.75rem",
                      mb: 1.5,
                    }}
                  >
                    Half {props.index} · {props.currentQuarter} · {props.currentYear}
                  </Typography>

                  {/* Date-time input */}
                  <TextField
                    type="datetime-local"
                    size="small"
                    fullWidth
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.85rem",
                        "& fieldset": { borderColor: "#067094" },
                        "&:hover fieldset": { borderColor: "#79DCFF" },
                        "&.Mui-focused fieldset": { borderColor: "#79DCFF" },
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: "invert(1)",
                      },
                    }}
                  />

                  {/* Feedback message */}
                  {extendMsg && (
                    <Typography
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.75rem",
                        color: extendMsg.includes("✓") ? "#4cffb3" : "#ff6b6b",
                        mb: 1,
                      }}
                    >
                      {extendMsg}
                    </Typography>
                  )}

                  {/* Buttons */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={extending}
                      onClick={handleExtendAll}
                      sx={{
                        flex: 1,
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        backgroundColor: "#067094",
                        "&:hover": { backgroundColor: "#079bc7" },
                        "&:disabled": { backgroundColor: "#14475D", color: "#555" },
                      }}
                    >
                      {extending ? "Updating…" : "Apply to All"}
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setAnchorEl(null);
                        setExtendMsg("");
                        setDeadline("");
                      }}
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        borderColor: "#4C4E4E",
                        color: "#aaa",
                        "&:hover": { borderColor: "#aaa", color: "#fff" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Popover>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CardDashboard;